import { useState, useEffect, useRef } from 'react';
import { User, CreditCard as Edit3, Mail, LogOut, Loader2, Shield, Languages, Download, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUserProfile } from '../hooks/useUserProfile';
import { EditProfileDialog } from './EditProfileDialog';
import { ChangeEmailDialog } from './ChangeEmailDialog';
import { LanguageDialog } from './LanguageDialog';
import { Portal } from './Portal';
import { userService } from '../services/userService';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { csvExportService } from '../services/csvExportService';
import { FinancialReport } from './FinancialReport';
import { log } from '../lib/logger';

export function ProfileMenu() {
  const navigate = useNavigate();
  const { user, profile, loading, refresh } = useUserProfile();
  const { t } = useLanguage();
  const { toast } = useModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [financialReportOpen, setFinancialReportOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    log.debug('[PROFILE-MENU] mounted');
  }, []);

  useEffect(() => {
    const loadAvatar = async () => {
      if (!profile?.avatar_url) {
        setAvatarUrl('');
        return;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(profile.avatar_url);

      setAvatarUrl(data.publicUrl);
    };

    loadAvatar();
  }, [profile?.avatar_url]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;
      const userRole = await userService.getCurrentUserRole();
      setIsAdmin(userRole?.role === 'admin');
    };
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return;

    log.debug('[PROFILE-MENU] open dropdown');

    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
    };

    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        const dropdown = document.querySelector('[data-testid="profile-dropdown"]');
        if (dropdown && !dropdown.contains(e.target as Node)) {
          setMenuOpen(false);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      log.error('Error signing out:', err);
      toast.error(t('profile.toast.signOutFailed'));
    }
  };

  const handleProfileSaved = async () => {
    await refresh();
    toast.success(t('profile.toast.profileUpdated'));
  };

  const handleLanguageSaved = () => {
    toast.success(t('profile.toast.profileUpdated'));
  };

  const handleExportCSV = async () => {
    setMenuOpen(false);
    setExportingCSV(true);

    try {
      await csvExportService.exportCampaignsToCSV();
      toast.success(t('profile.toast.exportSuccess'));
    } catch (error) {
      log.error('CSV export error:', error);
      toast.error(t('profile.toast.exportFailed'));
    } finally {
      setExportingCSV(false);
    }
  };

  if (loading) {
    return (
      <button
        type="button"
        disabled
        className="h-10 w-10 rounded-full bg-gray-200 animate-pulse flex items-center justify-center pointer-events-auto relative z-30"
        aria-label="Loading profile"
      >
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </button>
    );
  }

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-black/5 flex items-center justify-center text-sm bg-white/90 hover:bg-white transition pointer-events-auto relative z-30 shadow-md"
        aria-label="Sign in"
        title="Sign in"
        data-testid="profile-menu-trigger"
      >
        <User className="w-5 h-5 text-gray-600" />
      </button>
    );
  }

  const getInitials = () => {
    if (profile?.full_name) {
      const parts = profile.full_name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return profile.full_name[0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const displayName = profile?.full_name || user.user_metadata?.full_name || 'User';
  const displayEmail = user.email || '';

  return (
    <>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="h-10 w-10 rounded-full overflow-hidden ring-1 ring-black/5 hover:ring-2 hover:ring-blue-500 transition-all flex items-center justify-center text-sm font-medium bg-white/90 hover:bg-white pointer-events-auto relative z-30 shadow-md"
          aria-label="Open profile menu"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          data-testid="profile-menu-trigger"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
              <span>{getInitials()}</span>
            </div>
          )}
        </button>
        {isAdmin && (
          <div
            className="absolute -top-1 -right-1 h-5 w-5 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 rounded-full flex items-center justify-center ring-2 ring-white shadow-lg animate-pulse"
            title="Admin"
            aria-label="Admin user"
            style={{ animationDuration: '2s' }}
          >
            <Shield className="h-3 w-3 text-white drop-shadow-sm" strokeWidth={2.5} />
          </div>
        )}
      </div>

      {menuOpen && (
        <Portal>
          <div
            data-testid="profile-dropdown"
            className="fixed z-50 min-w-[18rem] rounded-xl border border-black/10 bg-white shadow-xl animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200 origin-top-right"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
            }}
            role="menu"
            aria-orientation="vertical"
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full overflow-hidden flex items-center justify-center">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                      <span>{getInitials()}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {displayName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {displayEmail}
                  </p>
                </div>
              </div>
            </div>

            <div className="py-1">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setEditProfileOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <Edit3 className="w-4 h-4 text-gray-400" />
                <span>{t('profile.menu.editProfile')}</span>
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  setChangeEmailOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{t('profile.menu.changeEmail')}</span>
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false);
                  setLanguageOpen(true);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <Languages className="w-4 h-4 text-gray-400" />
                <span>{t('profile.menu.language')}</span>
              </button>

              {isAdmin && (
                <button
                  onClick={handleExportCSV}
                  disabled={exportingCSV}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  role="menuitem"
                >
                  {exportingCSV ? (
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-gray-400" />
                  )}
                  <span>{exportingCSV ? t('profile.menu.exporting') : t('profile.menu.exportCampaigns')}</span>
                </button>
              )}

              {(isAdmin) && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setFinancialReportOpen(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                  role="menuitem"
                >
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span>Financial Report</span>
                </button>
              )}
            </div>

            <div className="border-t border-gray-100 py-1">
              <button
                onClick={handleSignOut}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('profile.menu.signOut')}</span>
              </button>
            </div>
          </div>
        </Portal>
      )}

      <EditProfileDialog
        open={editProfileOpen}
        onOpenChange={setEditProfileOpen}
        initialName={profile?.full_name || ''}
        initialAvatarPath={profile?.avatar_url || ''}
        onSaved={handleProfileSaved}
      />

      <ChangeEmailDialog
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
        currentEmail={displayEmail}
      />

      <LanguageDialog
        open={languageOpen}
        onOpenChange={setLanguageOpen}
        onSaved={handleLanguageSaved}
      />

      {financialReportOpen && (
        <Portal>
          <div className="fixed inset-0 z-[60] flex flex-col bg-white">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Financial Report</h2>
              </div>
              <button
                onClick={() => setFinancialReportOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close financial report"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <FinancialReport />
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
